import { useEffect, useRef, useState, useCallback } from 'react';
import { BASE_URL_API } from '../Constants';
import axios from 'axios';
import text from '../lib/dictionary.json';
import { get } from '../services/http';

// const MAX_NOTIFICATIONS_NUMBER = 1000;
export const PAGE_SIZE = 20;

const columnsMap = {
    hotelbooking: ['hosting_order', 'hotel_name', 'period', 'rooms_number', 'people_number'],
    ticket: ['hosting_order', 'from', 'to', 'departure_date', 'oneway', 'type'],
    meeting: ['title', 'hall_name', 'time', 'duration'],
    task: ['vehicle_palette_number', 'vehicle_class', 'driver_name', 'driver_phone', 'from', 'to', 'details']
};

const loadingState = { delete: false, mark: false, check: false };

export default function useNotifications(userId, lang, setAlertState, setReviewState, setAuto, setNotificationsQueue) {
    const [notifications, setNotifications] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loadingPage, setLoadingPage] = useState(false);
    const [unread, setUnread] = useState(0);

    const pageRef = useRef(1);
    const totalCountRef = useRef(0);
    const eventSourceRef = useRef(null);
    const fetchMore = useCallback(async () => {
        if (!userId || loadingPage || !hasMore) return;

        setLoadingPage(true);

        try {
            const currentPage = pageRef.current;

            const res = await get(`/notifications`, {
                    user_id: userId,
                    page: currentPage,
                    page_size: PAGE_SIZE
            });

            const { rows = [], count = 0, unread = 0 } = res.data || {};

            totalCountRef.current = count;

            setUnread(unread);

            setNotifications(prev => [
                ...prev,
                ...rows.map(n => ({ ...n, loading: loadingState }))
            ]);

            const loaded = currentPage * PAGE_SIZE;
            const more = loaded < count;

            setHasMore(more);
            pageRef.current += 1;
        } catch (err) {
            setAlertState({
                open: true,
                message: 'Failed to fetch notifications\n' + err.message,
                type: 'error'
            });
        } finally {
            setLoadingPage(false);
        }
    }, [userId, hasMore, loadingPage, setAlertState]);

    useEffect(() => {
        if (!userId) return;

        setNotifications([]);
        setHasMore(true);
        pageRef.current = 1;
        totalCountRef.current = 0;

    }, [userId]);

    useEffect(() => {
        if (!userId) return;

        let reconnectTimeout;

        const connect = () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }

            const es = new EventSource(
                `${BASE_URL_API}/notifications/stream?user_id=${userId}`,
                { withCredentials: true }
            );

            es.onmessage = (event) => {
                if (event.data.trim() === ': ping') return;
                try {
                    const data = JSON.parse(event.data);
                    if (data.type) {
                        const { type, displayMode, notification, unread_notifications } = data;



                        setUnread(unread_notifications);



                        switch (type) {

                            case 'create':

                                setNotifications(prev => [

                                    { ...notification, loading: loadingState },

                                    ...(prev.length >= PAGE_SIZE ? prev.slice(0, prev.length - 1) : prev)

                                ]);

                                totalCountRef.current += 1;



                                if (displayMode === 'popup') {

                                    setNotificationsQueue(prev => [...prev, notification]);

                                }



                                setTimeout(() => {

                                    setAlertState({

                                        open: true,

                                        message: text[lang]['new-notification'],

                                        type: 'info'

                                    });

                                }, 100);





                                break;



                            case 'update':

                                setNotifications(prev =>

                                    prev.map(n =>

                                        notification.id

                                            ? n.id === notification.id

                                                ? { ...notification, loading: loadingState }

                                                : n

                                            : notification.items.includes(n.id)

                                                ? { ...n, read: true, loading: loadingState }

                                                : n

                                    )

                                );

                                break;



                            case 'delete':

                                setNotifications(prev =>

                                    notification.id

                                        ? prev.filter(n => n.id !== notification.id)

                                        : prev.filter(n => !notification.items.includes(n.id))

                                );

                                break;

                            case 'delete-list':

                                setNotifications(prev =>

                                    prev.filter(n => !notification.ids.includes(n.id))

                                );

                                break;

                            case 'update-list':

                                setNotifications(prev =>

                                    prev.map(n => {

                                        const isUpdated = notification.ids.includes(n.id);

                                        return isUpdated ? {

                                            ...n, read: true, loading: loadingState

                                        } : n

                                    })

                                );

                                break;

                            default:

                                break;
                        }

                    } else if (data.auto) {
                        setAuto({ ...data.auto, loading: false });
                    } else {
                        setReviewState({
                            ...data,
                            columns: columnsMap[data.row_type],
                            open: true
                        });
                    }
                } catch (e) {
                    console.error("Error parsing SSE data", e);
                }
            };

            es.onerror = (err) => {
                console.error("EventSource failed:", err);
                es.close(); // إغلاق الاتصال الحالي فوراً عند الخطأ

                // محاولة إعادة الاتصال بعد 5 ثوانٍ
                clearTimeout(reconnectTimeout);
                reconnectTimeout = setTimeout(() => {
                    connect();
                }, 5000);
            };

            eventSourceRef.current = es;
        };

        connect();

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            clearTimeout(reconnectTimeout);
        };
    }, [userId, lang, setAlertState, setReviewState, setAuto]);

    // useEffect(() => {
    //     if (notifications.length > MAX_NOTIFICATIONS_NUMBER) {
    //         setAlertState({
    //             open: true,
    //             type: 'info',
    //             message: text[lang]['notification-exceed']
    //         });
    //     }
    // }, [notifications.length, lang, setAlertState]);

    return {
        notifications,
        setNotifications,
        resetPage: () => pageRef.current = 1,
        unread,
        fetchMore,
        hasMore,
        loadingPage
    };
}
